import React, { useEffect, useRef, useState } from 'react';
import { FiList, FiX } from 'react-icons/fi';
import '../../styles/RichTextComposer.css';

/**
 * Escapes plain text and wraps it as a quoted HTML block — used to turn a
 * forwarded thread/transcript into something that reads like a real
 * forwarded email (an indented, visually distinct block) instead of a
 * plain-text dump.
 */
export function toQuotedHtml(text: string): string {
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return `<blockquote class="rtc-quote">${escaped.replace(/\n/g, '<br>')}</blockquote>`;
}

/**
 * contentEditable often leaves a stray `<br>` or empty tag behind even when
 * there's no real content — `html === ''` isn't a reliable empty check.
 */
export function hasContent(html: string): boolean {
  const text = html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
  return text.length > 0;
}

/** Feather's icon set has no numbered-list glyph — authored to match its
 *  exact stroke convention (2px, round caps, 24x24) rather than reach for a
 *  mismatched substitute. */
const LinkIcon: React.FC<{ size?: number }> = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 15 15 9" />
    <path d="M10.5 6.5 12 5a4 4 0 0 1 5.6 5.6L16 12" />
    <path d="M13.5 17.5 12 19a4 4 0 0 1-5.6-5.6L8 12" />
  </svg>
);

const NumberedListIcon: React.FC<{ size?: number }> = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="10" y1="6" x2="21" y2="6" />
    <line x1="10" y1="12" x2="21" y2="12" />
    <line x1="10" y1="18" x2="21" y2="18" />
    <text x="3" y="8.5" fontSize="7" stroke="none" fill="currentColor">1</text>
    <text x="3" y="14.5" fontSize="7" stroke="none" fill="currentColor">2</text>
    <text x="3" y="20.5" fontSize="7" stroke="none" fill="currentColor">3</text>
  </svg>
);

interface RichTextComposerProps {
  /** Content to load into the editor. Only written into the DOM when
   *  `resetToken` changes — never on every keystroke, or the cursor would
   *  jump to the start on every render. */
  initialHtml: string;
  /** Change this to force the editor to reload `initialHtml` — e.g. when
   *  switching compose mode or switching which item is open. */
  resetToken: string | number;
  onChange: (html: string) => void;
  placeholder?: string;
  minRows?: number;
  disabled?: boolean;
}

type Cmd = 'bold' | 'italic' | 'underline' | 'insertUnorderedList' | 'insertOrderedList' | 'removeFormat';

const RichTextComposer: React.FC<RichTextComposerProps> = ({
  initialHtml, resetToken, onChange, placeholder, minRows = 4, disabled,
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const savedSelectionRef = useRef<Range | null>(null);

  useEffect(() => {
    if (editorRef.current) editorRef.current.innerHTML = initialHtml;
    setShowLinkInput(false);
    setLinkUrl('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetToken]);

  const fireChange = () => {
    if (editorRef.current) onChange(editorRef.current.innerHTML);
  };

  const runCmd = (cmd: Cmd) => {
    editorRef.current?.focus();
    document.execCommand(cmd);
    fireChange();
  };

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) savedSelectionRef.current = sel.getRangeAt(0).cloneRange();
  };

  const openLinkInput = () => {
    saveSelection();
    setLinkUrl('');
    setShowLinkInput(true);
  };

  const applyLink = () => {
    const url = linkUrl.trim();
    setShowLinkInput(false);
    if (!url) return;
    editorRef.current?.focus();
    const sel = window.getSelection();
    if (sel && savedSelectionRef.current) {
      sel.removeAllRanges();
      sel.addRange(savedSelectionRef.current);
    }
    // A collapsed selection (no highlighted text) needs something to turn
    // into a link — insert the URL itself as the link text.
    if (sel && sel.isCollapsed) {
      document.execCommand('insertText', false, url);
      const range = document.createRange();
      const node = sel.focusNode;
      if (node) {
        range.setStart(node, Math.max(0, (sel.focusOffset || 0) - url.length));
        range.setEnd(node, sel.focusOffset || 0);
        sel.removeAllRanges();
        sel.addRange(range);
      }
    }
    document.execCommand('createLink', false, /^https?:\/\//i.test(url) ? url : `https://${url}`);
    fireChange();
  };

  // Paste is stripped to plain text on purpose — preserving arbitrary pasted
  // HTML safely needs a sanitizer this app doesn't have; plain text avoids
  // that requirement entirely rather than half-handling it.
  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
    fireChange();
  };

  return (
    <div className={`rtc${disabled ? ' rtc--disabled' : ''}`}>
      <div className="rtc__toolbar" role="toolbar" aria-label="Formatting">
        <button type="button" className="rtc__btn rtc__btn--b" title="Bold" onMouseDown={e => e.preventDefault()} onClick={() => runCmd('bold')}>B</button>
        <button type="button" className="rtc__btn rtc__btn--i" title="Italic" onMouseDown={e => e.preventDefault()} onClick={() => runCmd('italic')}>I</button>
        <button type="button" className="rtc__btn rtc__btn--u" title="Underline" onMouseDown={e => e.preventDefault()} onClick={() => runCmd('underline')}>U</button>
        <span className="rtc__divider" aria-hidden="true" />
        <button type="button" className="rtc__btn" title="Bulleted list" onMouseDown={e => e.preventDefault()} onClick={() => runCmd('insertUnorderedList')}>
          <FiList size={13} aria-hidden="true" />
        </button>
        <button type="button" className="rtc__btn" title="Numbered list" onMouseDown={e => e.preventDefault()} onClick={() => runCmd('insertOrderedList')}>
          <NumberedListIcon />
        </button>
        <span className="rtc__divider" aria-hidden="true" />
        <button type="button" className="rtc__btn" title="Link" onMouseDown={e => e.preventDefault()} onClick={openLinkInput}>
          <LinkIcon />
        </button>
        <button type="button" className="rtc__btn" title="Remove formatting" onMouseDown={e => e.preventDefault()} onClick={() => runCmd('removeFormat')}>
          <FiX size={13} aria-hidden="true" />
        </button>
      </div>

      {showLinkInput && (
        <div className="rtc__link-popover">
          <input
            autoFocus
            value={linkUrl}
            onChange={e => setLinkUrl(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') { e.preventDefault(); applyLink(); }
              if (e.key === 'Escape') setShowLinkInput(false);
            }}
            placeholder="https://example.com"
            aria-label="Link URL"
          />
          <button type="button" className="rtc__link-apply" onMouseDown={e => e.preventDefault()} onClick={applyLink}>Add</button>
          <button type="button" className="rtc__link-cancel" onMouseDown={e => e.preventDefault()} onClick={() => setShowLinkInput(false)}>Cancel</button>
        </div>
      )}

      <div
        ref={editorRef}
        className="rtc__editor"
        style={{ minHeight: `${minRows * 1.5}em` }}
        contentEditable={!disabled}
        suppressContentEditableWarning
        data-placeholder={placeholder}
        onInput={fireChange}
        onPaste={handlePaste}
      />
    </div>
  );
};

export default RichTextComposer;
